<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/shared/PosSharedScrollable.vue
  // (dark: variants dropped; semantic tokens mapped to plain palette classes).
  import { computed, onMounted, ref, watch } from 'vue';
  import {
    useBreakpoints,
    useMediaQuery,
    useMutationObserver,
    useResizeObserver,
    useScroll,
  } from '@vueuse/core';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  type TOrientation = 'vertical' | 'horizontal';
  type TVariant = 'overlay' | 'pseudo';
  type TTheme = 'page' | 'elevated' | 'default';

  const props = defineProps<{
    orientation?: TOrientation;
    desktopOrientation?: TOrientation;
    offsetTop?: number;
    offsetBottom?: number;
    offsetLeft?: number;
    offsetRight?: number;
    variant?: TVariant;
    theme?: TTheme;
    size?: 'sm' | 'md' | 'lg' | number;
    edges?: Array<'top' | 'bottom' | 'left' | 'right'>;
    /** true hides it everywhere; 'touch' hides it on coarse-pointer devices only. */
    hideScrollbar?: boolean | 'touch';
    /** Show edge arrow buttons on fine-pointer devices when content overflows (horizontal only). */
    arrows?: boolean;
    scrollClass?: string;
  }>();

  const emit = defineEmits<{
    (e: 'scroll', ev: Event): void;
    (
      e: 'arrived-change',
      arrived: { top: boolean; bottom: boolean; left: boolean; right: boolean }
    ): void;
  }>();

  const containerRef = ref<HTMLElement>();
  const scrollRef = ref<HTMLElement>();

  const breakpoints = useBreakpoints({ md: 768 });
  const isDesktop = breakpoints.greaterOrEqual('md');

  const orientation = computed<TOrientation>(() => {
    if (props.desktopOrientation) {
      return isDesktop.value
        ? props.desktopOrientation
        : props.orientation ?? 'vertical';
    }
    return props.orientation ?? 'vertical';
  });

  const enabledEdges = computed(() => {
    if (props.edges && props.edges.length > 0) return props.edges;
    return orientation.value === 'vertical'
      ? ['top', 'bottom']
      : ['left', 'right'];
  });

  const sizePx = computed(() => {
    if (typeof props.size === 'number') return props.size;
    switch (props.size) {
      case 'sm':
        return 24;
      case 'lg':
        return 40;
      case 'md':
      default:
        return 32;
    }
  });

  const offsets = computed(() => ({
    top: props.offsetTop ?? 10,
    bottom: props.offsetBottom ?? 10,
    left: props.offsetLeft ?? 10,
    right: props.offsetRight ?? 10,
  }));

  const { arrivedState, measure } = useScroll(scrollRef, {
    offset: offsets.value,
  });

  // Touch devices scroll naturally by swiping; fine pointers (mouse) need
  // visible affordances (scrollbar or arrows) instead.
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  const hideScrollbarActive = computed(() => {
    if (props.hideScrollbar === 'touch') return isCoarsePointer.value;
    return !!props.hideScrollbar;
  });

  const showTop = computed(
    () => enabledEdges.value.includes('top') && !arrivedState.top
  );
  const showBottom = computed(
    () => enabledEdges.value.includes('bottom') && !arrivedState.bottom
  );
  const showLeft = computed(
    () => enabledEdges.value.includes('left') && !arrivedState.left
  );
  const showRight = computed(
    () => enabledEdges.value.includes('right') && !arrivedState.right
  );

  const themeClass = computed(() => {
    const t = props.theme ?? 'page';
    if (t === 'elevated') return 'theme-elevated';
    if (t === 'default') return 'theme-default';
    return 'theme-page';
  });
  const variant = computed<TVariant>(() => props.variant ?? 'overlay');

  //#region Scroll arrows

  const hasOverflow = ref(false);

  // Content can change (or resize) without emitting a scroll event, which
  // would leave both the arrows and the arrived state stale.
  function refreshArrowState() {
    if (!props.arrows) return;
    const el = scrollRef.value;
    if (!el) return;
    measure();
    hasOverflow.value =
      orientation.value === 'horizontal'
        ? el.scrollWidth > el.clientWidth + 1
        : el.scrollHeight > el.clientHeight + 1;
  }

  onMounted(refreshArrowState);
  watch(orientation, refreshArrowState, { flush: 'post' });
  useResizeObserver(scrollRef, refreshArrowState);
  useMutationObserver(scrollRef, refreshArrowState, {
    childList: true,
    subtree: true,
  });

  const arrowsActive = computed(
    () =>
      !!props.arrows &&
      orientation.value === 'horizontal' &&
      !isCoarsePointer.value &&
      hasOverflow.value
  );
  const showLeftArrow = computed(
    () => arrowsActive.value && !arrivedState.left
  );
  const showRightArrow = computed(
    () => arrowsActive.value && !arrivedState.right
  );

  function scrollByPage(direction: -1 | 1) {
    const el = scrollRef.value;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.8),
      behavior: 'smooth',
    });
  }

  //#endregion

  const handleScroll = (ev: Event) => {
    emit('scroll', ev);
    emit('arrived-change', { ...arrivedState });
  };

  defineExpose({
    arrivedState,
    containerRef,
    scrollRef,
    scrollToTop: () =>
      scrollRef.value?.scrollTo({ top: 0, behavior: 'smooth' }),
    scrollToBottom: () =>
      scrollRef.value?.scrollTo({
        top: scrollRef.value.scrollHeight,
        behavior: 'smooth',
      }),
    scrollToStart: () =>
      scrollRef.value?.scrollTo({ left: 0, behavior: 'smooth' }),
    scrollToEnd: () =>
      scrollRef.value?.scrollTo({
        left: scrollRef.value.scrollWidth,
        behavior: 'smooth',
      }),
    isVertical: computed(() => orientation.value === 'vertical'),
    isHorizontal: computed(() => orientation.value === 'horizontal'),
  });
</script>

<template>
  <div
    ref="containerRef"
    class="pos-scrollable"
    :class="[
      themeClass,
      variant === 'pseudo' ? 'pseudo' : 'overlay',
      orientation,
    ]"
  >
    <!-- Keep this node first: moving it when overlays toggle resets scrolling. -->
    <div
      key="scroll-content"
      ref="scrollRef"
      class="scroll"
      :class="[
        orientation,
        {
          'show-top-gradient': variant === 'pseudo' && showTop,
          'show-bottom-gradient': variant === 'pseudo' && showBottom,
          'show-left-gradient': variant === 'pseudo' && showLeft,
          'show-right-gradient': variant === 'pseudo' && showRight,
          'hide-scrollbar': hideScrollbarActive,
        },
        scrollClass,
      ]"
      @scroll="handleScroll"
    >
      <slot />
    </div>

    <!-- Overlay variant: absolute gradient nodes -->
    <template v-if="variant === 'overlay'">
      <slot v-if="showTop" name="overlay-top">
        <div
          key="gradient-top"
          class="gradient-top"
          :style="{ height: `${sizePx}px` }"
        />
      </slot>
      <slot v-if="showLeft" name="overlay-left">
        <div
          key="gradient-left"
          class="gradient-left"
          :style="{ width: `${sizePx}px` }"
        />
      </slot>
      <slot v-if="showBottom" name="overlay-bottom">
        <div
          key="gradient-bottom"
          class="gradient-bottom"
          :style="{ height: `${sizePx}px` }"
        />
      </slot>
      <slot v-if="showRight" name="overlay-right">
        <div
          key="gradient-right"
          class="gradient-right"
          :style="{ width: `${sizePx}px` }"
        />
      </slot>
    </template>

    <!-- Edge arrows (fine pointer + overflow only) -->
    <button
      v-if="showLeftArrow"
      type="button"
      class="scroll-arrow scroll-arrow-left"
      :aria-label="$t('posV2.scrollLeft')"
      @click="scrollByPage(-1)"
    >
      <PosUiIcon name="chevron-left" class="arrow-icon" />
    </button>
    <button
      v-if="showRightArrow"
      type="button"
      class="scroll-arrow scroll-arrow-right"
      :aria-label="$t('posV2.scrollRight')"
      @click="scrollByPage(1)"
    >
      <PosUiIcon name="chevron-right" class="arrow-icon" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .pos-scrollable {
    @apply tw-relative tw-overflow-hidden;
  }

  .scroll.vertical {
    @apply tw-h-full tw-overflow-y-auto;
  }

  .scroll.horizontal {
    @apply tw-w-full tw-overflow-x-auto;
  }

  .scroll.hide-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .scroll.hide-scrollbar::-webkit-scrollbar {
    @apply tw-hidden;
  }

  // Floating edge arrows: quiet white circles above the gradients (z-10).
  .scroll-arrow {
    @apply tw-absolute tw-top-1/2 tw-z-20 tw-flex tw-h-8 tw-w-8 tw--mt-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-neutral-200 tw-bg-surface tw-text-neutral-600 tw-shadow-sm tw-transition-colors tw-duration-150 hover:tw-bg-neutral-50 hover:tw-text-neutral-900;

    &.scroll-arrow-left {
      @apply tw-left-1.5;
    }

    &.scroll-arrow-right {
      @apply tw-right-1.5;
    }

    .arrow-icon {
      @apply tw-text-xl;
    }
  }

  /* Overlay gradients for page theme (white roots) */
  .overlay.theme-page .gradient-top {
    @apply tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-b tw-from-surface tw-to-transparent;
  }
  .overlay.theme-page .gradient-bottom {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10;
    @apply tw-bg-gradient-to-t tw-from-surface tw-to-transparent;
  }
  .overlay.theme-page .gradient-left {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-r tw-from-surface tw-to-transparent;
  }
  .overlay.theme-page .gradient-right {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-l tw-from-surface tw-to-transparent;
  }

  /* Overlay gradients for default theme (bg white in admin) */
  .overlay.theme-default .gradient-top {
    @apply tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-b tw-from-surface tw-to-transparent;
  }
  .overlay.theme-default .gradient-bottom {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10;
    @apply tw-bg-gradient-to-t tw-from-surface tw-to-transparent;
  }
  .overlay.theme-default .gradient-left {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-r tw-from-surface tw-to-transparent;
  }
  .overlay.theme-default .gradient-right {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-l tw-from-surface tw-to-transparent;
  }

  /* Overlay gradients for elevated theme (neutral-100) */
  .overlay.theme-elevated .gradient-top {
    @apply tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-b tw-from-neutral-100 tw-to-transparent;
  }
  .overlay.theme-elevated .gradient-bottom {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10;
    @apply tw-bg-gradient-to-t tw-from-neutral-100 tw-to-transparent;
  }
  .overlay.theme-elevated .gradient-left {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-r tw-from-neutral-100 tw-to-transparent;
  }
  .overlay.theme-elevated .gradient-right {
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10;
    @apply tw-bg-gradient-to-l tw-from-neutral-100 tw-to-transparent;
  }

  /* Pseudo variant - sticky pseudo elements toggled by classes (vertical) */
  .pseudo .scroll.vertical {
    @apply tw-relative;
  }
  .pseudo .scroll.vertical::before,
  .pseudo .scroll.vertical::after {
    content: '';
    @apply tw-pointer-events-none tw-sticky tw-left-0 tw-right-0 tw-z-10;
    @apply tw-block tw-opacity-0 tw-transition-opacity tw-duration-200;
    height: 32px;
  }
  .pseudo.theme-elevated .scroll.vertical::before {
    @apply tw-top-0 tw--mb-8 tw-bg-gradient-to-b tw-from-neutral-100 tw-to-transparent;
  }
  .pseudo.theme-elevated .scroll.vertical::after {
    @apply tw-bottom-0 tw--mt-8 tw-bg-gradient-to-t tw-from-neutral-100 tw-to-transparent;
  }
  .pseudo.theme-page .scroll.vertical::before {
    @apply tw-top-0 tw--mb-8 tw-bg-gradient-to-b tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-page .scroll.vertical::after {
    @apply tw-bottom-0 tw--mt-8 tw-bg-gradient-to-t tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-default .scroll.vertical::before {
    @apply tw-top-0 tw--mb-8 tw-bg-gradient-to-b tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-default .scroll.vertical::after {
    @apply tw-bottom-0 tw--mt-8 tw-bg-gradient-to-t tw-from-surface tw-to-transparent;
  }
  .pseudo .scroll.vertical.show-top-gradient::before {
    @apply tw-opacity-100;
  }
  .pseudo .scroll.vertical.show-bottom-gradient::after {
    @apply tw-opacity-100;
  }

  /* Pseudo variant - horizontal */
  .pseudo .scroll.horizontal::before,
  .pseudo .scroll.horizontal::after {
    content: '';
    @apply tw-pointer-events-none tw-absolute tw-bottom-0 tw-top-0 tw-z-10;
    @apply tw-opacity-0 tw-transition-opacity tw-duration-200;
    width: 32px;
  }
  .pseudo.theme-elevated .scroll.horizontal::before {
    @apply tw-left-0 tw-bg-gradient-to-r tw-from-neutral-100 tw-to-transparent;
  }
  .pseudo.theme-elevated .scroll.horizontal::after {
    @apply tw-right-0 tw-bg-gradient-to-l tw-from-neutral-100 tw-to-transparent;
  }
  .pseudo.theme-page .scroll.horizontal::before {
    @apply tw-left-0 tw-bg-gradient-to-r tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-page .scroll.horizontal::after {
    @apply tw-right-0 tw-bg-gradient-to-l tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-default .scroll.horizontal::before {
    @apply tw-left-0 tw-bg-gradient-to-r tw-from-surface tw-to-transparent;
  }
  .pseudo.theme-default .scroll.horizontal::after {
    @apply tw-right-0 tw-bg-gradient-to-l tw-from-surface tw-to-transparent;
  }
  .pseudo .scroll.horizontal.show-left-gradient::before {
    @apply tw-opacity-100;
  }
  .pseudo .scroll.horizontal.show-right-gradient::after {
    @apply tw-opacity-100;
  }
</style>
